/** The HTTP client. */

export const API_BASE = '/api/v1'

/** Which app is asking. */
export type ClientApp = 'member' | 'admin'
const CLIENT_APP_HEADER = 'x-client-app'
let clientApp: ClientApp = 'member'

/** Called once, before the first request, by the app's entry. */
export function setClientApp(app: ClientApp): void {
  clientApp = app
  siblings()
}

export interface ApiErrorPayload {
  code: string
  message: string
  /** Rendered beside the input it names, never in a banner over the form. */
  fields?: Record<string, string>
  /** retryAfterSeconds, lockedUntil, requiredStatus, missingPermission, caseNo. */
  meta?: Record<string, unknown>
  traceId: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly fields?: Record<string, string>
  readonly traceId: string
  readonly meta?: Record<string, unknown>

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message)
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code
    this.fields = payload.fields
    this.traceId = payload.traceId
    this.meta = payload.meta
  }

  is(code: string): boolean {
    return this.code === code
  }

  /** Seconds to wait before retrying, when the server said so. */
  get retryAfterSeconds(): number | null {
    const v = this.meta?.retryAfterSeconds
    return typeof v === 'number' ? v : null
  }
}

const NETWORK_ERROR: ApiErrorPayload = {
  code: 'NETWORK',
  message: 'ارتباط با سامانه برقرار نشد. اتصال خود را بررسی کنید.',
  traceId: '-',
}

type TokenListener = (token: string | null) => void

class TokenStore {
  private access: string | null = null
  private listeners = new Set<TokenListener>()

  get(): string | null {
    return this.access
  }

  set(token: string | null) {
    this.access = token
    for (const l of this.listeners) l(token)
  }

  subscribe(l: TokenListener): () => void {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }
}

export const tokens = new TokenStore()

/** A signed in member whose session could not be renewed. */
type ExpiryListener = () => void
const expiryListeners = new Set<ExpiryListener>()
let expiryAnnounced = false
tokens.subscribe((token) => {
  if (token) expiryAnnounced = false
})

function announceExpired() {
  if (expiryAnnounced) return
  expiryAnnounced = true
  for (const l of expiryListeners) l()
}

export function onSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.add(listener)
  return () => expiryListeners.delete(listener)
}

/** How a renewal ended. */
type RefreshOutcome = 'ok' | 'refused' | 'unavailable'

let refreshInFlight: Promise<RefreshOutcome> | null = null

type SiblingNotice = { kind: 'renewing' } | { kind: 'renewed'; token: string | null }

/** The browser offers the lock only on https and on localhost. */
const SIBLING_WAIT_MS = 10_000

let siblingChannel: BroadcastChannel | null = null
let siblingChannelApp: ClientApp | null = null
let siblingRenewingUntil = 0
const siblingWaiters = new Set<() => void>()

/** The channel this app's tabs talk on. */
function siblings(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (siblingChannel && siblingChannelApp === clientApp) return siblingChannel
  siblingChannel?.close()
  siblingChannel = new BroadcastChannel(`metal-session-${clientApp}`)
  siblingChannelApp = clientApp
  siblingChannel.onmessage = (event: MessageEvent<SiblingNotice>) => heard(event.data)
  return siblingChannel
}
siblings()

function heard(notice: SiblingNotice) {
  if (notice.kind === 'renewing') {
    siblingRenewingUntil = Date.now() + SIBLING_WAIT_MS
    return
  }
  siblingRenewingUntil = 0
  // Taken by a tab that is signed in, or that is renewing right now:
  const current = tokens.get()
  if (notice.token && notice.token !== current && (current !== null || refreshInFlight)) {
    tokens.set(notice.token)
  }
  for (const wake of siblingWaiters) wake()
}

/** Resolves once no sibling is known to be renewing. */
function siblingSettled(): Promise<void> {
  const wait = siblingRenewingUntil - Date.now()
  if (wait <= 0) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer)
      siblingWaiters.delete(done)
      resolve()
    }
    const timer = window.setTimeout(done, wait)
    siblingWaiters.add(done)
  })
}

/** One renewal at a time across the app's tabs. */
async function oneAtATime(task: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
  const run = async () => {
    await siblingSettled()
    return task()
  }
  const locks = (navigator as { locks?: LockManager }).locks
  return locks ? locks.request(`metal-refresh-${clientApp}`, run) : run()
}

/** A token newer than the one a refused request carried. */
function renewedSince(sent: string | null): boolean {
  const current = tokens.get()
  return current !== null && current !== sent
}

/** Exchange the refresh cookie for a new access token. */
async function refresh(sent: string | null): Promise<RefreshOutcome> {
  if (renewedSince(sent)) return 'ok'
  if (!refreshInFlight) {
    // Asked again under the lock:
    const task = async (): Promise<RefreshOutcome> => (renewedSince(sent) ? 'ok' : renew())
    refreshInFlight = oneAtATime(task).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/** The refresh route itself, and word of it to the siblings. */
async function renew(): Promise<RefreshOutcome> {
  const channel = siblings()
  channel?.postMessage({ kind: 'renewing' } satisfies SiblingNotice)

  let outcome: RefreshOutcome
  let token: string | null = null
  try {
    const res = await fetch(`${API_BASE}/auth/session/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', [CLIENT_APP_HEADER]: clientApp },
    })
    if (res.ok) {
      token = ((await res.json()) as { accessToken: string }).accessToken
      outcome = 'ok'
    } else {
      // The route refuses with 401 or 403.
      outcome = res.status === 401 || res.status === 403 ? 'refused' : 'unavailable'
    }
  } catch {
    outcome = 'unavailable'
  }

  if (outcome === 'ok') tokens.set(token)
  if (outcome === 'refused') tokens.set(null)
  channel?.postMessage({ kind: 'renewed', token } satisfies SiblingNotice)
  return outcome
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Skip the automatic refresh and retry. Used by the auth routes themselves. */
  raw?: boolean
  /** Send no token and never renew. */
  anonymous?: boolean
  headers?: Record<string, string>
}

/** One fetch with the token, renewed once on a 401. */
async function authorizedFetch(
  url: string,
  init: RequestInit,
  raw = false,
  anonymous = false,
): Promise<Response> {
  const send = async (token: string | null): Promise<Response> => {
    try {
      return await fetch(url, {
        ...init,
        credentials: 'include',
        headers: {
          [CLIENT_APP_HEADER]: clientApp,
          ...(token ? { authorization: `Bearer ${token}` } : {}),
          ...(init.headers as Record<string, string> | undefined),
        },
      })
    } catch {
      throw new ApiError(0, NETWORK_ERROR)
    }
  }

  if (anonymous) return send(null)

  const sent = tokens.get()
  const res = await send(sent)
  if (res.status !== 401 || raw) return res

  const outcome = await refresh(sent)
  // Nobody answered for the cookie, so the session stands.
  if (outcome === 'unavailable') throw new ApiError(0, NETWORK_ERROR)
  if (outcome === 'refused') {
    if (sent !== null) announceExpired()
    return res
  }
  return send(tokens.get())
}

/** A refusal read off a response that did not succeed. */
async function refusal(res: Response, fallback: string): Promise<ApiError> {
  const text = await res.text().catch(() => '')
  let envelope: ApiErrorPayload | null = null
  try {
    envelope = text ? (JSON.parse(text) as ApiErrorPayload) : null
  } catch {
    envelope = null
  }
  return new ApiError(
    res.status,
    envelope?.code ? envelope : { code: 'INTERNAL', message: fallback, traceId: '-' },
  )
}

/** A stand in for the server, for a build that answers part of the API from data it carries. */
export interface LocalRequest {
  method: string
  /** Under the API base, without the query: `/offers/1048`. */
  path: string
  query: URLSearchParams
  kind: 'json' | 'upload' | 'download'
}
export type LocalApi = (req: LocalRequest) => Promise<unknown> | undefined
let localApi: LocalApi | null = null

export function setLocalApi(handler: LocalApi | null): void {
  localApi = handler
}

function local(method: string, url: URL, kind: LocalRequest['kind']): Promise<unknown> | undefined {
  if (!localApi) return undefined
  return localApi({ method, path: url.pathname.slice(API_BASE.length), query: url.searchParams, kind })
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, raw, anonymous, headers = {} } = options
  const url = new URL(`${API_BASE}${path}`, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
    }
  }
  const answered = local(method, url, 'json')
  if (answered) return answered as Promise<T>

  const res = await authorizedFetch(
    url.toString(),
    {
      method,
      signal,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    },
    raw,
    anonymous,
  )

  if (res.status === 204) return undefined as T
  if (!res.ok) throw await refusal(res, 'خطای غیرمنتظره رخ داد.')

  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/** Upload one or more files as multipart. */
export async function upload<T>(
  path: string,
  files: File[],
  fields: Record<string, string> = {},
  fieldName = 'file',
): Promise<T> {
  const form = new FormData()
  for (const f of files) form.append(fieldName, f)
  for (const [k, v] of Object.entries(fields)) form.append(k, v)
  const answered = local('POST', new URL(`${API_BASE}${path}`, window.location.origin), 'upload')
  if (answered) return answered as Promise<T>

  const res = await authorizedFetch(`${API_BASE}${path}`, { method: 'POST', body: form })
  if (!res.ok) throw await refusal(res, 'بارگذاری انجام نشد.')
  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

/** A protected file, as bytes. */
export async function download(path: string): Promise<Blob> {
  const answered = local('GET', new URL(`${API_BASE}${path}`, window.location.origin), 'download')
  if (answered) return answered as Promise<Blob>
  const res = await authorizedFetch(`${API_BASE}${path}`, { method: 'GET' })
  if (!res.ok) throw await refusal(res, 'دریافت فایل انجام نشد.')
  return res.blob()
}

/** The collection envelope. */
export interface Paged<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  pageCount: number
  facets?: Record<string, unknown>
}
